/**
 * The recorded room's small sums: the clock, which chapter and which sentence the playhead is in, and which frame
 * of the seek sprite shows a second. Pure, so the room and its tests agree.
 */
import type { HeroFrames, WatchCaption, WatchChapter, WatchMessage } from "../../../shared/types";
import type { Arrivals } from "../TalkPlayer";

/** "07:05": minutes and seconds, as the prototype's player reads. */
export function mmss(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** The chapter the playhead is in: the last one that has started, or the first before any has. */
export function chapterAt(chapters: readonly WatchChapter[], t: number): number {
  let at = 0;
  chapters.forEach((c, i) => {
    if (c.t <= t) at = i;
  });
  return at;
}

/** Where a chapter ends: where the next begins, or the talk's end. */
export function chapterEnd(chapters: readonly WatchChapter[], i: number, total: number): number {
  return chapters[i + 1]?.t ?? total;
}

/** How long a line stays on screen after it is said, when the next one is a while away. */
const LINE_HOLD_SEC = 42;

/** The sentence being said at `t`: the last one started, or null before the first. */
export function lineAt(captions: readonly WatchCaption[], t: number): WatchCaption | null {
  let found: WatchCaption | null = null;
  for (const c of captions) {
    if (c.t > t) break;
    found = c;
  }
  return found;
}

/** The words on screen at `t`: the current sentence while it is fresh, nothing in a long pause. */
export function captionAt(captions: readonly WatchCaption[], t: number): string {
  const line = lineAt(captions, t);
  return line && t - line.t < LINE_HOLD_SEC ? line.text : "";
}

/**
 * The CSS that shows the seek sprite's frame for second `t` in a box of the tile's shape, at any width: the sheet is
 * sized in percent of the box and the frame picked by percent, so a filmstrip tile and the scrub preview both use it.
 */
export function frameStyle(frames: HeroFrames, t: number): { backgroundImage: string; backgroundSize: string; backgroundPosition: string } {
  const index = Math.max(0, Math.min(frames.count - 1, Math.floor(t / Math.max(1, frames.interval_sec))));
  const cols = Math.max(1, frames.columns);
  const rows = Math.max(1, Math.ceil(frames.count / cols));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const at = (n: number, of: number) => (of > 1 ? `${(n / (of - 1)) * 100}%` : "0%");
  return {
    backgroundImage: `url("${frames.url}")`,
    backgroundSize: `${cols * 100}% ${rows * 100}%`,
    backgroundPosition: `${at(col, cols)} ${at(row, rows)}`,
  };
}

/** The speeds the room plays at, from 1× to 2×. */
export const SPEEDS: readonly number[] = [1, 1.25, 1.5, 1.75, 2];

export function speedLabel(s: number): string {
  return `${s}×`;
}

/**
 * How a player menu fits the screen, from its own height and the room it has on each side. One that opens down under
 * the bar turns up when only that side has the room; either way it is never taller than its side, and scrolls past
 * that, so every item stays on screen to be tapped (the prototype let the open offer and the dock cover them).
 */
export function menuRoom(natural: number, below: number, above: number, opensUp: boolean): { flip: boolean; max: number | null } {
  const capped = (room: number) => (natural <= room ? null : Math.max(0, Math.floor(room)));
  if (opensUp) return { flip: false, max: capped(above) };
  if (natural <= below) return { flip: false, max: null };
  if (above > below) return { flip: true, max: capped(above) };
  return { flip: false, max: capped(below) };
}

/**
 * Does the moment on screen hold the talk? While it waits for an answer, and (as the prototype tells it) through its
 * thanks too: the talk carries on only once the card has gone.
 */
export function holdsTalk(arrivals: Pick<Arrivals, "up" | "paused">): boolean {
  return arrivals.paused || (!!arrivals.up && arrivals.up.show.pause && arrivals.up.my_response !== null);
}

/** His oldest question from this recording that the host has not answered yet: a line with a moment and no host line after it. */
export function waitingQuestion(messages: readonly WatchMessage[]): WatchMessage | null {
  const lastHost = messages.reduce((at, m) => (m.sender === "host" ? Math.max(at, m.created_at) : at), 0);
  const asked = messages.filter((m) => m.sender !== "host" && m.at_sec !== undefined && m.created_at > lastHost);
  return asked.reduce<WatchMessage | null>((first, m) => (!first || m.created_at < first.created_at ? m : first), null);
}
